import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { HttpClientModule, HttpClientXsrfModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CategoriesListComponent } from './features/categories/categories-list.component';
import { CategoryFormComponent } from './features/categories/category-form.component';
import { AuthorizationGuard } from './core/guards/authorization.guard';
import { LoginComponent } from './auth/login.component';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';

import { AppComponent } from './app.component';
import { FornecedoresListComponent } from './features/fornecedores/list/fornecedores-list.component';
import { FornecedorDetailComponent } from './features/fornecedores/detail/fornecedor-detail.component';
import { FornecedorFormComponent } from './features/fornecedores/form/fornecedor-form.component';
import { MediaManagerComponent } from './features/fornecedores/media/media-manager.component';
import { FornecedorCardComponent } from './features/fornecedores/components/fornecedor-card.component';
import { FornecedorPreviewComponent } from './features/fornecedores/preview/fornecedor-preview.component';
import { SpinnerComponent } from './shared/components/spinner.component';
import { ToastComponent } from './shared/components/toast.component';
import { ModalConfirmComponent } from './shared/components/modal-confirm.component';
import { PaginationComponent } from './shared/components/pagination.component';
import { EmptyStateComponent } from './shared/components/empty-state.component';

import { ErrorInterceptor } from './core/interceptors/error.interceptor';
import { RateLimitInterceptor } from './core/interceptors/rate-limit.interceptor';
import { Component } from '@angular/core';
import { TestemunhoService } from './features/testemunhos/testemunho.service';
import { TestemunhoManagerComponent } from './features/testemunhos/testemunho-manager.component';
import { NotificationService } from './core/notification.service';
import { DashboardComponent } from './features/dashboard/dashboard.component';
import { NewsletterListComponent } from './features/newsletter/newsletter-list.component';
import { BlogListComponent } from './features/blog/blog-list.component';
import { BlogFormComponent } from './features/blog/blog-form.component';
import { AdesoesListComponent } from './features/adesoes/list/adesoes-list.component';
import { LeadsListComponent } from './features/leads/leads-list.component';
import { LgpdListComponent } from './features/lgpd/lgpd-list.component';
import { BannersListComponent } from './features/banners/banners-list.component';
import { BannerFormComponent } from './features/banners/banner-form.component';
import { LeadsAuditComponent } from './features/leads/leads-audit.component';
import { LgpdQueueComponent } from './features/lgpd/lgpd-queue.component';
import { BannersManagerComponent } from './features/banners/banners-manager.component';
import { AuditLogListComponent } from './features/audit-log/audit-log-list.component';
import { AssinaturasListComponent } from './features/financeiro/assinaturas-list.component';
import { RegistrarPagamentoComponent } from './features/financeiro/registrar-pagamento.component';
import { HistoricoListComponent } from './features/financeiro/historico-list.component';
import { FornecedoresAdminListComponent } from './features/fornecedores/admin-list/fornecedores-admin-list.component';
import { InstitucionalListComponent } from './features/institucional/institucional-list.component';
import { InstitucionalFormComponent } from './features/institucional/institucional-form.component';
import { TermAcceptanceListComponent } from './features/term-acceptances/list/term-acceptance-list.component';
import { TermAcceptanceDetailComponent } from './features/term-acceptances/detail/term-acceptance-detail.component';
import { SupplierPaymentListComponent } from './features/supplier-payments/list/supplier-payment-list.component';
import { SupplierPaymentDetailComponent } from './features/supplier-payments/detail/supplier-payment-detail.component';
import { CouponsListComponent } from './features/coupons/coupons-list.component';
import { FornecedorAdminDetailComponent } from './features/fornecedores/admin-detail/fornecedor-admin-detail.component';
import { DestaquesSemanaListComponent } from './features/destaques-semana/destaques-semana-list.component';
import { DestaqueSemanaFormComponent } from './features/destaques-semana/destaque-semana-form.component';
import { CidadesListComponent } from './features/cidades/list/cidades-list.component';
import { CidadeFormComponent } from './features/cidades/form/cidade-form.component';

// Admin testimonials list component (simple implementation)
@Component({
  selector: 'app-admin-testemunhos-list',
  template: `
    <div class="bg-white p-4 rounded shadow">
      <h2 class="text-2xl font-semibold mb-4">💬 Testemunhos</h2>

      <!-- Filters -->
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">Buscar por Fornecedor ID</label>
          <input [(ngModel)]="fornecedorId" name="fornecedorId" placeholder="ID do Fornecedor"
            class="w-full border border-border rounded px-3 py-2 text-sm"/>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">Status</label>
          <select [(ngModel)]="statusFilter" class="w-full border border-border rounded px-3 py-2 text-sm">
            <option value="">Todos</option>
            <option value="published">📢 Publicados</option>
            <option value="draft">📝 Rascunhos</option>
          </select>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-600 mb-1">Avaliação</label>
          <select [(ngModel)]="ratingFilter" class="w-full border border-border rounded px-3 py-2 text-sm">
            <option value="">Todas</option>
            <option value="5">⭐⭐⭐⭐⭐ (5)</option>
            <option value="4">⭐⭐⭐⭐ (4)</option>
            <option value="3">⭐⭐⭐ (3)</option>
            <option value="2">⭐⭐ (2)</option>
            <option value="1">⭐ (1)</option>
          </select>
        </div>
        <div class="flex items-end gap-2">
          <button type="button" (click)="filter()" class="flex-1 px-3 py-2 bg-primary text-white rounded text-sm">🔍 Filtrar</button>
          <button type="button" (click)="resetFilter()" class="flex-1 px-3 py-2 border rounded text-sm hover:bg-gray-50">✕ Limpar</button>
        </div>
      </div>

      <div *ngIf="loading" class="text-sm text-center py-8">Carregando...</div>
      <div *ngIf="!loading && items.length === 0" class="text-sm text-muted text-center py-8">Nenhum testemunho encontrado.</div>

      <div class="space-y-3" *ngIf="items.length > 0">
        <div *ngFor="let t of items" class="border border-border rounded p-4 hover:shadow-sm transition">
          <div class="flex justify-between items-start gap-3">
            <div class="flex-1 min-w-0">
              <!-- Header row: name + badges -->
              <div class="flex flex-wrap items-center gap-2 mb-1">
                <div class="font-semibold text-gray-900">{{ t.nome }}</div>
                <!-- Rating stars -->
                <div *ngIf="t.rating" class="flex items-center gap-0.5">
                  <span *ngFor="let s of getStars(t.rating)" class="text-amber-400 text-sm">★</span>
                  <span *ngFor="let s of getEmptyStars(t.rating)" class="text-gray-300 text-sm">★</span>
                </div>
                <!-- Published badge -->
                <span [ngClass]="t.isPublished ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'"
                  class="px-2 py-0.5 rounded-full text-xs font-medium">
                  {{ t.isPublished ? '📢 Publicado' : '📝 Rascunho' }}
                </span>
              </div>
              <!-- Meta info -->
              <div class="text-xs text-muted mb-2">
                <span *ngIf="t.fornecedorNome || t.fornecedorId">🏢 {{ t.fornecedorNome || t.fornecedorId }}</span>
                <span *ngIf="t.email"> · ✉️ {{ t.email }}</span>
                <span> · {{ t.createdAt | date:'dd/MM/yyyy' }}</span>
              </div>
              <!-- Content -->
              <p class="text-sm text-gray-700 leading-relaxed overflow-hidden" style="display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical;">{{ t.descricao }}</p>
            </div>
            <!-- Actions -->
            <div class="flex flex-col gap-1 flex-shrink-0">
              <button (click)="togglePublish(t)"
                class="text-xs px-2 py-1 rounded border hover:bg-gray-50"
                [ngClass]="t.isPublished ? 'border-orange-400 text-orange-600' : 'border-green-500 text-green-700'">
                {{ t.isPublished ? '🔒 Despublicar' : '📢 Publicar' }}
              </button>
              <button (click)="remove(t)" class="text-xs px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600">🗑️ Remover</button>
            </div>
          </div>
        </div>
      </div>

      <div class="mt-4 flex items-center justify-between" *ngIf="canPaginate">
        <button (click)="prev()" [disabled]="page===1" class="px-3 py-1 border rounded disabled:opacity-50">← Anterior</button>
        <span class="text-sm text-muted">Página {{ page }} de {{ totalPages }}</span>
        <button (click)="next()" [disabled]="page>=totalPages" class="px-3 py-1 border rounded disabled:opacity-50">Próxima →</button>
      </div>
    </div>
  `
  , standalone: false
})
export class AdminTestemunhosListComponent {
  items: any[] = [];
  page = 1;
  pageSize = 20;
  totalPages = 1;
  fornecedorId: string = '';
  statusFilter: string = '';
  ratingFilter: string = '';
  loading = false;
  constructor(private api: TestemunhoService,
    private notification: NotificationService) {
    this.load();
  }
  load() {
    this.loading = true;
    this.api.adminList(this.page, this.pageSize, this.fornecedorId || undefined, this.statusFilter || undefined, this.ratingFilter ? parseInt(this.ratingFilter) : undefined).subscribe({
      next: (res: any) => {
        this.loading = false;
        this.items = res?.data || [];
        this.totalPages = res?.meta?.totalPages || 1;
      },
      error: (err: any) => {
        this.loading = false;
        console.error('[AdminTestemunhos] erro ao listar', err);
        this.notification.showError('Erro ao listar testemunhos');
      }
    });
  }
  filter() { this.page = 1; this.load(); }
  resetFilter() { this.fornecedorId = ''; this.statusFilter = ''; this.ratingFilter = ''; this.filter(); }
  prev() { if (this.page > 1) { this.page--; this.load(); } }
  next() { if (this.page < this.totalPages) { this.page++; this.load(); } }
  get canPaginate() { return this.totalPages > 1; }
  remove(t: any) {
    if (!confirm('Remover este testemunho?')) return;
    this.api.adminDelete(t.id).subscribe({
      next: () => {
        this.items = this.items.filter(i => i.id !== t.id);
        this.notification.showSuccess('Removido');
      },
      error: (err: any) => {
        console.error('[AdminTestemunhos] erro ao remover', err);
        this.notification.showError('Erro ao remover testemunho');
      }
    });
  }
  togglePublish(t: any) {
    const newVal = !t.isPublished;
    this.api.adminTogglePublish(t.id, newVal).subscribe({
      next: () => {
        t.isPublished = newVal;
        this.notification.showSuccess(newVal ? 'Testemunho publicado' : 'Testemunho despublicado');
      },
      error: (err: any) => {
        console.error('[AdminTestemunhos] erro ao alterar publicação', err);
        this.notification.showError('Erro ao alterar publicação');
      }
    });
  }
  getStars(rating: number): number[] { return Array(Math.min(rating, 5)).fill(0); }
  getEmptyStars(rating: number): number[] { return Array(Math.max(5 - rating, 0)).fill(0); }
}

@NgModule({
  declarations: [
    AppComponent,
    FornecedoresListComponent,
    FornecedorDetailComponent,
    FornecedorFormComponent,
    MediaManagerComponent,
    FornecedorCardComponent,
    ToastComponent,
    ModalConfirmComponent,
    EmptyStateComponent,
    CategoriesListComponent,
    CategoryFormComponent,
    AdminTestemunhosListComponent,
    TestemunhoManagerComponent,
    DashboardComponent,
    FornecedorPreviewComponent,
    NewsletterListComponent,
    BlogListComponent,
    BlogFormComponent,

    LeadsListComponent,
    LgpdListComponent,
    AssinaturasListComponent,
    RegistrarPagamentoComponent,
    HistoricoListComponent,
    BannersListComponent,
    BannerFormComponent,
    AuditLogListComponent,
    FornecedoresAdminListComponent,
    InstitucionalListComponent,
    InstitucionalFormComponent,
    TermAcceptanceListComponent,
    TermAcceptanceDetailComponent,
    SupplierPaymentListComponent,
    SupplierPaymentDetailComponent,
    CouponsListComponent,
    FornecedorAdminDetailComponent,
    DestaquesSemanaListComponent,
    DestaqueSemanaFormComponent,
    CidadesListComponent,
    CidadeFormComponent
  ],
  imports: [
    BrowserModule,
    CommonModule,
    HttpClientModule,
    HttpClientXsrfModule.withOptions({ cookieName: 'XSRF-TOKEN', headerName: 'X-XSRF-TOKEN' }),
    FormsModule,
    ReactiveFormsModule,
    SpinnerComponent,
    PaginationComponent,
    LeadsAuditComponent,
    LgpdQueueComponent,
    BannersManagerComponent,
    AdesoesListComponent,
    RouterModule.forRoot([
      { path: '', redirectTo: '/admin', pathMatch: 'full' },
      { path: 'admin', component: DashboardComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'fornecedores', component: FornecedoresListComponent },
      { path: 'admin/fornecedores', component: FornecedoresAdminListComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'fornecedores/:slug', component: FornecedorDetailComponent },
      { path: 'admin/fornecedores/new', component: FornecedorFormComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/fornecedores/:id/detail', component: FornecedorAdminDetailComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/fornecedores/:id/preview', component: FornecedorPreviewComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/fornecedores/:id/edit', component: FornecedorFormComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/categories', component: CategoriesListComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/categories/new', component: CategoryFormComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/categories/:id/edit', component: CategoryFormComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/media', component: MediaManagerComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/testemunhos', component: AdminTestemunhosListComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/leads', component: LeadsListComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/lgpd', component: LgpdQueueComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/banners', component: BannersManagerComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/newsletter', component: NewsletterListComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/blog', component: BlogListComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/blog/new', component: BlogFormComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/blog/:id/edit', component: BlogFormComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/leads/:id', loadComponent: () => import('./features/leads/lead-detalhe.component').then(m => m.LeadDetalheComponent), canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/financeiro/assinaturas', component: AssinaturasListComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/assinaturas', component: AssinaturasListComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/financeiro/registrar-pagamento', component: RegistrarPagamentoComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/financeiro/historico', component: HistoricoListComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/config/banners', component: BannersListComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/config/banners/new', component: BannerFormComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/config/banners/:id/edit', component: BannerFormComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/audit-log', component: AuditLogListComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/institucional', component: InstitucionalListComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/institucional/:key/edit', component: InstitucionalFormComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/resource/term-acceptances', component: TermAcceptanceListComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/term-acceptances', component: TermAcceptanceListComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/term-acceptances/:id', component: TermAcceptanceDetailComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/supplier-payments', component: SupplierPaymentListComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/supplier-payments/:id', component: SupplierPaymentDetailComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/coupons', component: CouponsListComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/adesoes', component: AdesoesListComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/adesoes/:id', loadComponent: () => import('./features/adesoes/detail/adesoes-detail.component').then(m => m.AdesoesDetailComponent), canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/adesoes/:id/auditoria', loadComponent: () => import('./features/adesoes/auditoria/adesoes-auditoria.component').then(m => m.AdesoesAuditoriaComponent), canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/adesoes/:id/relatorio', loadComponent: () => import('./features/adesoes/relatorio/adesoes-relatorio.component').then(m => m.AdesoesRelatorioComponent), canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/destaques-semana', component: DestaquesSemanaListComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/destaques-semana/new', component: DestaqueSemanaFormComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/destaques-semana/:id/edit', component: DestaqueSemanaFormComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/cidades', component: CidadesListComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/cidades/new', component: CidadeFormComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'admin/cidades/:id/edit', component: CidadeFormComponent, canActivate: [AuthorizationGuard], data: { roles: ['Admin'] } },
      { path: 'auth/login', loadComponent: () => import('./auth/login.component').then(m => m.LoginComponent) },
      { path: 'access-denied', loadComponent: () => import('./auth/access-denied.component').then(m => m.AccessDeniedComponent) },
      { path: '**', redirectTo: '' }
    ])
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: RateLimitInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
